// frontend/modules/customer-notification/components/trigger/CustomNotificationTrigger.tsx
import React, { useState } from 'react';
import { Card, Form, Input, Button, Alert, Switch, Space, Divider, Typography, Result, Spin, Collapse, Tabs, Radio } from 'antd';
import { SendOutlined, EditOutlined, CheckCircleOutlined, MessageOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNotificationTrigger } from '../../hooks/useNotificationTrigger';
import CustomerSegmentSelector from '../common/CustomerSegmentSelector';
import PlatformSelector from '../common/PlatformSelector';
import NotificationTemplateSelector from '../common/NotificationTemplateSelector';
import { NotificationPlatforms } from '@/shared/api/model';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;
const { TabPane } = Tabs;

interface CustomNotificationTriggerProps {
    onClose: () => void;
}

const CustomNotificationTrigger: React.FC<CustomNotificationTriggerProps> = ({ onClose }) => {
    const [form] = Form.useForm();
    const [customerFilters, setCustomerFilters] = useState<Record<string, any>>({});
    const [testMode, setTestMode] = useState(true);
    const [contentType, setContentType] = useState<'custom' | 'template'>('custom');
    const [platform, setPlatform] = useState<NotificationPlatforms>(NotificationPlatforms.SMS);

    const {
        isLoading,
        sendCustomBulkSmsNotifications,
        sendTemplatedNotifications,
        resultSummary
    } = useNotificationTrigger();

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (contentType === 'custom') {
                // Send custom message
                await sendCustomBulkSmsNotifications(
                    values.messageText,
                    customerFilters,
                    testMode,
                    values.platform
                );
            } else {
                // Send template-based message
                await sendTemplatedNotifications(
                    values.templateName,
                    values.platform,
                    customerFilters,
                    values.templateParameters ? JSON.parse(values.templateParameters) : {},
                    testMode
                );
            }
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    const handleCustomerFiltersChange = (filters: Record<string, any>) => {
        setCustomerFilters(filters);
    };

    const handlePlatformChange = (value: NotificationPlatforms) => {
        setPlatform(value);
        form.setFieldsValue({ platform: value });
    };

    // Show success result if we have sent notifications
    if (resultSummary) {
        return (
            <Card>
                <Result
                    status="success"
                    title="Messages Sent Successfully"
                    subTitle={`${resultSummary.total} notifications sent at ${new Date(resultSummary.timestamp).toLocaleString()}`}
                    icon={<CheckCircleOutlined />}
                    extra={[
                        <Button type="primary" key="done" onClick={onClose}>
                            Done
                        </Button>
                    ]}
                />
            </Card>
        );
    }

    return (
        <Card title={
            <Space>
                <EditOutlined />
                <span>Custom Notification</span>
            </Space>
        }>
            {isLoading ? (
                <div className="text-center py-12">
                    <Spin size="large" />
                    <div className="mt-4">
                        <Text>Processing messages...</Text>
                    </div>
                </div>
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        platform: NotificationPlatforms.SMS
                    }}
                >
                    <Alert
                        message="Custom Notifications"
                        description="Send custom or template-based messages to selected customers across different platforms."
                        type="info"
                        showIcon
                        className="mb-6"
                    />

                    <Form.Item
                        name="platform"
                        label="Platform"
                        rules={[{ required: true, message: 'Please select a platform' }]}
                    >
                        <PlatformSelector
                            value={platform}
                            onChange={handlePlatformChange}
                        />
                    </Form.Item>

                    <Divider>Message Content</Divider>

                    <Radio.Group
                        value={contentType}
                        onChange={e => setContentType(e.target.value)}
                        className="mb-4"
                        buttonStyle="solid"
                    >
                        <Radio.Button value="custom">
                            <Space>
                                <EditOutlined />
                                <span>Custom Message</span>
                            </Space>
                        </Radio.Button>
                        <Radio.Button value="template">
                            <Space>
                                <FileTextOutlined />
                                <span>Use Template</span>
                            </Space>
                        </Radio.Button>
                    </Radio.Group>

                    {contentType === 'custom' ? (
                        <Form.Item
                            name="messageText"
                            rules={[
                                { required: true, message: 'Please enter message text' },
                                {
                                    max: platform === NotificationPlatforms.SMS ? 160 : 2000,
                                    message: platform === NotificationPlatforms.SMS ?
                                        'SMS messages should be 160 characters or less for best delivery' :
                                        'Message is too long'
                                }
                            ]}
                            tooltip={platform === NotificationPlatforms.SMS ?
                                "Enter the SMS message text. Keep it under 160 characters to avoid splitting into multiple messages." :
                                "Enter your message content"
                            }
                        >
                            <TextArea
                                rows={5}
                                placeholder="Enter your message text here..."
                                showCount
                                maxLength={platform === NotificationPlatforms.SMS ? 500 : 2000}
                            />
                        </Form.Item>
                    ) : (
                        <>
                            <Form.Item
                                name="templateName"
                                label="Message Template"
                                rules={[{ required: true, message: 'Please select a template' }]}
                            >
                                <NotificationTemplateSelector
                                    platform={platform}
                                    placeholder="Select a template"
                                />
                            </Form.Item>

                            <Form.Item
                                name="templateParameters"
                                label="Template Parameters (JSON)"
                                tooltip="Enter parameters as JSON object, e.g. {'key': 'value'}"
                            >
                                <TextArea
                                    rows={4}
                                    placeholder='{"param1": "value1", "param2": "value2"}'
                                />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item
                        label="Test Mode"
                        tooltip="In test mode, the message will only be sent to a small sample of the selected customers."
                    >
                        <Switch
                            checked={testMode}
                            onChange={(checked) => setTestMode(checked)}
                            checkedChildren="On"
                            unCheckedChildren="Off"
                        />
                        <Text className="ml-2" type={testMode ? 'success' : 'secondary'}>
                            {testMode
                                ? 'Test mode is ON - Only sending to a small sample of customers'
                                : 'Test mode is OFF - Will send to ALL selected customers'}
                        </Text>
                    </Form.Item>

                    <Divider orientation="left">Target Audience</Divider>

                    <Collapse defaultActiveKey={['1']} className="mb-6">
                        <Panel header="Customer Segment Filters" key="1">
                            <CustomerSegmentSelector onChange={handleCustomerFiltersChange} />
                        </Panel>
                    </Collapse>

                    {!testMode && (
                        <Alert
                            message="Warning: Production Mode"
                            description="You are about to send messages to ALL customers matching your filters. This action cannot be undone."
                            type="warning"
                            showIcon
                            className="mb-6"
                        />
                    )}

                    <Form.Item className="mb-0">
                        <Space>
                            <Button
                                type="primary"
                                danger={!testMode}
                                onClick={handleSubmit}
                                icon={<SendOutlined />}
                            >
                                {testMode ? 'Send Test Messages' : 'Send to All Recipients'}
                            </Button>
                            <Button onClick={onClose}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            )}
        </Card>
    );
};

export default CustomNotificationTrigger;